import React from 'react';
import { motion } from 'framer-motion';
import { useGradientBackground } from '@/utils/gradientBackground';
import TypewriterText from '@/components/lib/ui/TypewriterText';

export default function AboutHero() {
  // Background gradient
  const backgroundStyle = useGradientBackground();

  // Animation variants for entrance
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
        staggerChildren: 0.12,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const founderStripVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.4,
      },
    },
  };

  const slideFromLeft = {
    hidden: { opacity: 0, x: -30 },
    show: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  const slideFromRight = {
    hidden: { opacity: 0, x: 30 },
    show: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <section className="relative w-full py-24 md:py-32 overflow-hidden" style={backgroundStyle}>
      {/* Subtle radial overlays for depth */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-64 w-64 bg-gradient-radial from-[#0000FF]/20 via-transparent to-transparent blur-3xl opacity-80" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 h-64 w-64 bg-gradient-radial from-[#0000FF]/15 via-transparent to-transparent blur-3xl opacity-60" />
      {/* Bottom fade to white */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-white" />

      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Top Zone: Label + Title + Subtitle */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="max-w-3xl mx-auto text-center relative z-10 mb-12 md:mb-16"
          >
            <motion.div variants={itemVariants}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6">
                I build the AI systems that run businesses. This one runs on mine.
              </h1>
            </motion.div>

            <motion.div variants={itemVariants}>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl mx-auto">
                Let me show you...
              </p>
            </motion.div>
          </motion.div>

          {/* Lower Zone: Founder Strip */}
          <motion.div
            variants={founderStripVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            className="mt-12 max-w-4xl mx-auto relative z-10"
          >
            <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
              {/* Left: Stylized Avatar */}
              <motion.div variants={slideFromLeft} className="flex-shrink-0 text-center">
                {/* Avatar with gradient ring */}
                <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-full p-[2px] bg-gradient-to-br from-[#0000FF] via-[#4D7DFF] to-slate-800 mx-auto mb-4">
                  <img
                    src="/media/services/adam-avatar.png"
                    alt="Adam, founder of Attain Domains"
                    className="w-full h-full rounded-full object-cover"
                  />
                </div>
              </motion.div>

              {/* Right: Typewriter intro block */}
              <motion.div
                variants={slideFromRight}
                className="flex-1 text-center md:text-left space-y-4"
              >
                {/* Typewriter line */}
                <div className="text-xl md:text-2xl font-semibold text-foreground leading-relaxed">
                  <TypewriterText
                    text="Hello, I'm Adam :)"
                    speedMs={50}
                  />
                </div>

                {/* Supporting paragraph */}
                <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                  I build AI systems that handle the day-to-day of a business, so the owner gets back to the work only they can do.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

